/**
 * Contracts Redux Slice
 * 
 * Estado y acciones para la gestión de contratos
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
export interface Contract {
  id: number;
  contract_number: string;
  entity_id: number;
  carrier_id: number | null;
  currency_id: number | null;
  status_id: number | null;
  valid_from: string | null;
  valid_to: string | null;
  operation_type: string | null;
  total_teus_committed: number;
  total_teus_used: number;
  total_value_estimated: number;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
// Tipos de respuesta
interface ContractsResponse {
  data: Contract[];
  totalCount: number;
}
interface ContractsState extends FilteredState<Contract> {
  selectedContract: Contract | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
}
const initialState: ContractsState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'contract_number',
  sortDirection: 'desc',
  searchTerm: null,
  selectedContract: null,
  creating: false,
  updating: false,
  deleting: false,
};
export const fetchContracts = createAsyncThunk<ContractsResponse, { pagination?: PaginationParams; sort?: SortParams; filters?: FilterParams }>(
  'contracts/fetchContracts',
  async (params) => {
    throw new Error('Not implemented yet');
  }
);
export const fetchContractById = createAsyncThunk<Contract, number>(
  'contracts/fetchContractById',
  async (id) => {
    throw new Error('Not implemented yet');
  }
);
export const createContract = createAsyncThunk<Contract, Partial<Contract>>(
  'contracts/createContract',
  async (contract) => {
    throw new Error('Not implemented yet');
  }
);
export const updateContract = createAsyncThunk<{ id: number; data: Contract }, { id: number; data: Partial<Contract> }>(
  'contracts/updateContract',
  async ({ id, data }) => {
    // TODO: Implementar llamada a API
    return { id, data: { ...data, id } as Contract };
  }
);
export const deleteContract = createAsyncThunk<number, number>(
  'contracts/deleteContract',
  async (id) => {
    // TODO: Implementar llamada a API
    return id;
  }
);
const contractsSlice = createSlice({
  name: 'contracts',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectContract: (state, action: PayloadAction<Contract | null>) => {
      state.selectedContract = action.payload;
    },
    clearSelection: (state) => {
      state.selectedContract = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContracts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContracts.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
      })
      .addCase(fetchContracts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar contratos';
      });
    builder
      .addCase(fetchContractById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContractById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedContract = action.payload;
        state.lastFetch = Date.now();
      })
      .addCase(fetchContractById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar contrato';
      });
    builder
      .addCase(createContract.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createContract.fulfilled, (state, action) => {
        state.creating = false;
        state.items.unshift(action.payload);
        state.totalCount += 1;
      })
      .addCase(createContract.rejected, (state, action) => {
        state.creating = false;
        state.error = action.error.message || 'Error al crear contrato';
      });
    builder
      .addCase(updateContract.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateContract.fulfilled, (state, action) => {
        state.updating = false;
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload.data;
        }
        if (state.selectedContract?.id === action.payload.id) {
          state.selectedContract = action.payload.data;
        }
      })
      .addCase(updateContract.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || 'Error al actualizar contrato';
      });
    builder
      .addCase(deleteContract.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteContract.fulfilled, (state, action) => {
        state.deleting = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
        state.totalCount -= 1;
        if (state.selectedContract?.id === action.payload) {
          state.selectedContract = null;
        }
      })
      .addCase(deleteContract.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.error.message || 'Error al eliminar contrato';
      });
  },
});
export const {
  setFilters,
  clearFilters,
  setSearchTerm,
  setSort,
  setPage,
  setPageSize,
  selectContract,
  clearSelection,
} = contractsSlice.actions;
export default contractsSlice.reducer;
